import { brand } from '../../theme/themePrimitives';
import { DropResult } from 'react-beautiful-dnd';
import RenderEmptyState from './render_empty_state';
import RenderStages from '../stage/render_stages';
import { settingSelectedStage } from '../../redux/slices/stage';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, Button } from '@mui/material';
import { handleDealReorder, handleStageReorder } from '../../helper/pipelineHelper';
import { KeyboardArrowLeftOutlined, KeyboardArrowRightOutlined } from '@mui/icons-material';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: PipelineContent Component
 * @paragraph: This component renders the content of a pipeline, including stages and deals.
 * It handles drag-and-drop functionality for reordering stages and deals, and displays a loading overlay during updates.
 */

/**
* @interface: PipelineContentProps
* @property: {function} setAddStage - Function to toggle the modal for adding a new stage.
*/
interface PipelineContentProps {
    setAddStage: (value: boolean) => void;
    pipelineRef: React.RefObject<HTMLDivElement>;
}

/**
 * @heading2: PipelineContent
 * @paragraph: Main component for rendering pipeline content. Includes drag-and-drop reordering for stages and deals, and empty state handling.
 * @param: {PipelineContentProps} props - Props containing the `setAddStage` function.
 */
const PipelineContent: React.FC<PipelineContentProps> = ({ setAddStage }) => {
    /**
     * @heading3: Variables
     * @ol:
     * disaptch: {AppDisptach} - The Redux dispatch function to trigger actions.
     * isLoading: {boolean} - Check for drag and drop.
     * selectedDeal/notes/currentUser: An object containing the details from the Redux store.
     */
    const dispatch = useDispatch<AppDispatch>();
    const pipelineRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const { pipelines, selectedPipeline, loaders, bulkEditMode } = useSelector((state: RootState) => state.pipeline);
    const hasStages = selectedPipeline?.pipeline_stages?.length !== 0;
    const disabled = loaders.isCreating || loaders.isDeleting || loaders.isLoading || loaders.isUpdating;

    /**
     * @heading3: Functions
     * @heading4: handleDragEnd
     * @paragraph: Handles the end of a drag-and-drop operation. Reorders stages or deals based on the drag result.
     * @param: {DropResult} result - The result of the drag-and-drop operation.
     */
    const handleDragEnd = async (result: DropResult) => {
        // setIsLoading(true);
        const { source, destination, type } = result;
        if (!destination) return;
        if (!selectedPipeline || !selectedPipeline.pipeline_stages) {
            console.error('Selected pipeline or pipeline stages are missing');
            return;
        }
        if (type === 'stage') {
            await handleStageReorder(source, destination, selectedPipeline, dispatch);
        } else if (type === 'deal') {
            await handleDealReorder(source, destination, selectedPipeline, dispatch);
        }
        // setIsLoading(false);
    };

    const handleScroll = (direction: 'left' | 'right') => {
        if (pipelineRef.current) {
            const scrollAmount = 350;
            pipelineRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const updateScrollButtons = () => {
        if (pipelineRef.current) {
            setCanScrollLeft(pipelineRef.current.scrollLeft > 0);
            setCanScrollRight(pipelineRef.current.scrollLeft + pipelineRef.current.clientWidth < pipelineRef.current.scrollWidth);
        }
    };


    useEffect(() => {
        updateScrollButtons();
        if (pipelineRef.current) {
            pipelineRef.current.addEventListener('scroll', updateScrollButtons);
        }
        window.addEventListener('resize', updateScrollButtons);
        return () => {
            if (pipelineRef.current) {
                pipelineRef.current.removeEventListener('scroll', updateScrollButtons);
            }
            window.removeEventListener('resize', updateScrollButtons);
        };
    }, [selectedPipeline]);

    /**
     * @heading4: Conditional Rendering
     * - Displays a loading overlay if `isLoading` is true.
     * - Renders empty state or stages based on the selected pipeline and its stages.
     */
    // if (isLoading) {
    //     return (
    //         <Overlay>
    //             <CircularProgress size={24} />
    //             <Typography ml={2}>Update in Progress...</Typography>
    //         </Overlay>
    //     );
    // }

    const addStageHandler = () => {
        dispatch(settingSelectedStage(null));
        setAddStage(true);
    };

    if (disabled) { return <></>; }

    return (
        <Box>
            {selectedPipeline && <Box display="flex" justifyContent={selectedPipeline && 'end'} alignItems="center">
                {(canScrollLeft || canScrollRight) && <Box display="flex" gap={2}>
                    <Button variant="outlined" color="primary" onClick={() => handleScroll('left')}><KeyboardArrowLeftOutlined /></Button>
                    <Button variant="outlined" color="primary" onClick={() => handleScroll('right')}><KeyboardArrowRightOutlined /></Button>
                </Box>}
            </Box>}
            <Box
                display="flex"
                ref={pipelineRef}
                position="relative"
                maxHeight="66vh"
                minHeight="66vh"
                sx={{
                    'overflow': 'auto',
                    'overflowX': 'auto',
                    'scrollbarWidth': 'thin',
                    'scrollbarColor': brand[300],
                    '&::-webkit-scrollbar': {
                        width: '8px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: brand[300],
                        borderRadius: '4px'
                    },
                    '&:hover::-webkit-scrollbar-thumb': {
                        backgroundColor: brand[600]
                    }
                }}
            >
                {
                    // Render Stages list
                    hasStages
                        ? (
                            <RenderStages
                                handleDragEnd={handleDragEnd}
                                setAddStage={() => setAddStage(true)}
                            />
                        )
                        // Render Empty Stage Page
                        : (
                            <RenderEmptyState
                                isPipeline={false}
                                disabled={disabled}
                                onSubmit={addStageHandler}
                            />
                        )}
                {/* Render Add stage Action Button */}
                {hasStages && pipelines.length !== 0 && selectedPipeline !== null && !bulkEditMode && (
                    <Box>
                        <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={{
                                marginTop: 2,
                                textTransform: 'none',
                                padding: '6px 12px',
                                whiteSpace: 'nowrap',
                                width: '300px'
                            }}
                            onClick={addStageHandler}
                            disabled={disabled}
                        >
                            + Add a Stage
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default PipelineContent;
